import styled from 'styled-components';

const PRIMARY_RED = '#E31B23';
const PRIMARY_RED_SOFT = 'rgba(227, 27, 35, 0.08)';
const BORDER = '#eef0f3';
const BG = '#f6f8fb';
const TEXT = '#1f2937';
const MUTED = '#6b7280';

export const BuilderContainer = styled.div`
  /* Fill the parent admin layout instead of guessing chrome height with
     100vh, which caused the outer page to scroll on shorter screens.
     The min-height keeps the form usable even if parent height is auto. */
  height: 100%;
  min-height: 600px;
  max-height: 100%;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: ${BG};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid ${BORDER};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

export const TopBar = styled.div`
  background: #fff;
  padding: 16px 24px;
  border-bottom: 1px solid ${BORDER};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  /* Allow the action buttons to wrap to a second row on narrow screens
     instead of overflowing off the right edge. */
  flex-wrap: wrap;
  row-gap: 12px;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  /* Each Space group becomes a flex item — let them shrink and wrap
     instead of pushing siblings off-screen. */
  > .ant-space {
    flex-wrap: wrap;
    row-gap: 8px;
    min-width: 0;
  }

  .title-input {
    border: 1px solid ${BORDER};
    font-size: 20px;
    font-weight: 600;
    color: ${TEXT};
    background: #fff;
    /* Shrink with the viewport so the title doesn't shove the buttons
     off-screen on smaller widths. */
    width: clamp(180px, 32vw, 360px);
    padding: 6px 12px;
    border-radius: 8px;
    transition: all 0.15s ease;

    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
    }
    &:focus {
      background: #fff;
      border-color: ${PRIMARY_RED};
      box-shadow: 0 0 0 3px ${PRIMARY_RED_SOFT};
      outline: none;
    }
  }

  .ant-tag {
    font-weight: 600;
    letter-spacing: 0.5px;
    border-radius: 999px;
    padding: 2px 12px;
    font-size: 11px;
    border: none;
  }

  .ant-btn {
    border-radius: 8px;
    height: 38px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
  }
`;

export const TabsContainer = styled.div`
  background: #fff;
  border-bottom: 1px solid ${BORDER};
  padding: 8px 24px 0;
  flex-shrink: 0;

  .ant-tabs {
    margin: 0 !important;
    background: transparent;
  }

  .ant-tabs-nav {
    margin: 0 !important;

    &::before {
      display: none !important;
    }
  }

  /* Hide ink bar — pill style instead */
  .ant-tabs-ink-bar {
    display: none !important;
  }

  .ant-tabs-nav-wrap {
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }

  .ant-tabs-nav-list {
    display: flex;
    gap: 4px;
    align-items: center;
    padding-bottom: 8px;
  }

  .ant-tabs-tab {
    position: relative;
    padding: 0 !important;
    margin: 0 !important;
    background: transparent;
    border: none;
    transition: all 0.2s ease;

    &::before { display: none; }

    + .ant-tabs-tab { margin-left: 0 !important; }

    .ant-tabs-tab-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 14px;
      border-radius: 999px;
      color: ${MUTED};
      font-weight: 500;
      font-size: 14px;
      transition: background-color .2s ease, color .2s ease;
      background: transparent;
      border: 1px solid transparent;
      min-height: 40px;
      white-space: nowrap;

      .anticon { font-size: 16px; }
    }

    &:hover .ant-tabs-tab-btn {
      color: ${TEXT};
      background: rgba(24, 144, 255, 0.08);
    }

    &.ant-tabs-tab-active .ant-tabs-tab-btn {
      color: #1890ff;
      background: #eef4ff;
      font-weight: 600;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
      border: 1px solid rgba(24, 144, 255, 0.3);
    }

    .ant-tabs-tab-btn:focus-visible {
      outline: 2px solid #1890ff;
      outline-offset: 2px;
      border-radius: 999px;
    }
  }
`;

export const ContentArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  > .builder-inner {
    padding: 32px;
    max-width: 980px;
    width: 100%;
    margin: 0 auto;
  }
`;

export const QuestionCard = styled.div`
  position: relative;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  border: 1px solid ${BORDER};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: all 0.2s ease;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-radius: 12px 0 0 12px;
    background: ${(props) => (props.$active ? PRIMARY_RED : 'transparent')};
    transition: background 0.2s ease;
  }

  &:hover {
    border-color: #d9dde3;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
    transform: translateY(-1px);
  }

  ${(props) => props.$active && `
    border-color: ${PRIMARY_RED};
    box-shadow: 0 4px 16px rgba(227, 27, 35, 0.10);
  `}

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector,
  .ant-input-number {
    border-radius: 8px;
  }
`;

export const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  .number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 10px;
    border-radius: 8px;
    background: ${PRIMARY_RED_SOFT};
    color: ${PRIMARY_RED};
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.5px;
  }

  .ant-btn-text {
    border-radius: 6px;
    color: ${MUTED};

    &:hover {
      background: #f3f4f6;
      color: ${TEXT};
    }
  }
`;

export const AddQuestionBar = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 22px;
  text-align: center;
  border: 2px dashed #d9dde3;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${MUTED};
  font-weight: 500;
  font-size: 14px;

  .anticon {
    margin-right: 8px;
    font-size: 16px;
  }

  &:hover {
    border-color: ${PRIMARY_RED};
    background: ${PRIMARY_RED_SOFT};
    color: ${PRIMARY_RED};
    transform: translateY(-1px);
  }
`;

export const BrandingCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  margin-bottom: 20px;
  border: 1px solid ${BORDER};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  h3 {
    margin: 0 0 6px;
    color: ${TEXT};
    font-size: 16px;
    font-weight: 600;
  }

  > p {
    margin: 0 0 20px;
    color: ${MUTED};
    font-size: 13px;
  }

  label {
    color: ${TEXT};
    font-size: 13px;
    font-weight: 500;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector,
  .ant-input-number {
    border-radius: 8px;
  }
`;
